import { useCallback, useEffect, useState } from 'react'
import { brandAssets } from '@/config/brandAssets'
import { lookupCustomDomain, type CustomDomainLookupResult } from '@/lib/customDomainLookup'
import { setCustomDomainTenant } from '@/lib/tenantHost'
import { CustomDomainNotFound } from './CustomDomainNotFound'

type GateState = 'checking' | 'found' | 'not-found' | 'unreachable' | 'retrying'

const lookupThisHost = (): Promise<CustomDomainLookupResult> =>
  lookupCustomDomain(window.location.hostname)

/**
 * On a customer's own domain (hr.acme.com) the tenant is not in the hostname,
 * so ask the API which workspace this domain serves before the app renders:
 * routing, the login page and the wrong-tenant redirect all depend on it.
 * "Workspace not found" only when the API says so (404); if it can't be
 * reached, say that and offer a retry.
 */
export function CustomDomainGate({
  children,
  lookup = lookupThisHost,
}: {
  children: React.ReactNode
  lookup?: () => Promise<CustomDomainLookupResult>
}): React.ReactElement | null {
  const [state, setState] = useState<GateState>('checking')

  const apply = useCallback((result: CustomDomainLookupResult): void => {
    if (result.status === 'found') setCustomDomainTenant(result.slug)
    setState(result.status)
  }, [])

  useEffect(() => {
    let cancelled = false
    void lookup().then((result) => {
      if (!cancelled) apply(result)
    })
    return () => {
      cancelled = true
    }
  }, [lookup, apply])

  if (state === 'found') return <>{children}</>
  if (state === 'not-found') return <CustomDomainNotFound />
  if (state === 'checking') return null
  return (
    <CustomDomainUnreachable
      retrying={state === 'retrying'}
      onRetry={() => {
        setState('retrying')
        void lookup().then(apply)
      }}
    />
  )
}

/** Rendered without the router or API client, like CustomDomainNotFound. */
function CustomDomainUnreachable({
  retrying,
  onRetry,
}: {
  retrying: boolean
  onRetry: () => void
}): React.ReactElement {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-3 bg-white px-6 text-center">
      <img src={brandAssets.logo} alt="KAAMCO" className="mb-4 h-auto w-40 object-contain" />
      <h1 className="text-xl font-bold text-gray-900">We couldn&apos;t reach KAAMCO</h1>
      <p className="max-w-md text-sm text-gray-600">
        Check your internet connection and try again. If it keeps happening, KAAMCO may be briefly
        unavailable.
      </p>
      <button
        type="button"
        onClick={onRetry}
        disabled={retrying}
        className="mt-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-60"
      >
        {retrying ? 'Retrying…' : 'Retry'}
      </button>
    </div>
  )
}
